import React from 'react';
import { Button, SxProps, Theme, Tooltip } from '@mui/material';
import { Chat as ChatIcon, Launch as LaunchIcon } from '@mui/icons-material';
// ボタンタイプの定義
export type ButtonType = 'default' | 'chat';

// テーマカラーの型定義
export interface ThemeColor {
  main: string;    // メインカラー（ボタンのテキスト色、ボーダー色）
  hover: string;   // ホバー時の背景色
}

// コンポーネントのプロパティ定義
export interface ExternalLinkButtonProps {
  href: string;
  label?: string;
  tooltip?: string | React.ReactNode;
  tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right';
  size?: 'small' | 'medium' | 'large';
  variant?: 'text' | 'outlined' | 'contained';
  color?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  sx?: SxProps<Theme>;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  buttonType?: ButtonType;
  themeColor?: ThemeColor;
  className?: string;
}

// ボタンタイプの設定のインターフェース
interface ButtonTypeConfig {
  defaultLabel: string;
  defaultTooltip: string;
  getStartIcon: (size: string) => React.ReactNode;
  getEndIcon: () => React.ReactNode;
  getSxProps: (size: string) => SxProps<Theme>;
}

// ボタンタイプ別の設定
const BUTTON_TYPE_CONFIG: Record<ButtonType, ButtonTypeConfig> = {
  chat: {
    defaultLabel: '開く',
    defaultTooltip: 'アプリ(Web)でチャットを表示',
    getStartIcon: (size: string) => <ChatIcon fontSize={size === 'small' ? 'small' : 'medium'}/>,
    getEndIcon: () => undefined,
    getSxProps: (size: string): SxProps<Theme> => ({
      fontWeight: 'medium',
      fontSize: size === 'small' ? '0.8rem' : '0.875rem',
      whiteSpace: 'nowrap',
      boxShadow: 1
    })
  },
  default: {
    defaultLabel: 'アプリで開く',
    defaultTooltip: 'アプリページを開く',
    getStartIcon: () => undefined,
    getEndIcon: () => <LaunchIcon/>,
    getSxProps: (size: string): SxProps<Theme> => ({
      ml: 2,
      px: 2,
      fontWeight: 'bold',
      boxShadow: 1
    })
  }
};
/**
 * 外部リンクボタンコンポーネント
 * アプリケーション外部へのリンクを表示するボタン
 */
const ExternalLinkButton: React.FC<ExternalLinkButtonProps> = ({
  href,
  label,
  tooltip,
  tooltipPlacement = 'top',
  size = 'small',
  variant = 'outlined',
  color = 'primary',
  sx = {},
  startIcon: customStartIcon,
  endIcon: customEndIcon,
  onClick,
  buttonType = 'default',
  themeColor,
  className
}) => {
  // ボタンタイプの設定を取得
  const config = BUTTON_TYPE_CONFIG[buttonType];
  // ラベルとツールチップの設定
  const finalLabel = label ?? config.defaultLabel;
  const finalTooltip = tooltip ?? config.defaultTooltip;
  // アイコンの設定
  const defaultStartIcon = config.getStartIcon?.(size);
  const defaultEndIcon = config.getEndIcon?.();
  const finalStartIcon = customStartIcon !== undefined ? customStartIcon : defaultStartIcon;
  const finalEndIcon = customEndIcon !== undefined ? customEndIcon : defaultEndIcon;
  // スタイルの設定
  const baseSx = config.getSxProps?.(size) || {};
  // テーマカラーがある場合のスタイル
  const themeColorSx = themeColor ? {
    borderColor: themeColor.main,
    color: themeColor.main,
    '&:hover': {
      backgroundColor: themeColor.hover,
      borderColor: themeColor.main,
      boxShadow: 2
    }
  } : {
    '&:hover': {
      ...(buttonType === 'chat' ? { backgroundColor: 'rgba(25, 118, 210, 0.08)' } : {}),
      boxShadow: 2
    }
  };
  // 最終的なスタイル - TypeScriptが理解できる形で結合
  const buttonSx: SxProps<Theme> = { 
    ...baseSx as object, 
    ...themeColorSx as object, 
    ...sx as object 
  };
  return (
    <Tooltip title={finalTooltip} arrow placement={tooltipPlacement}>
      <Button
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        variant={variant}
        color={color}
        size={size}
        startIcon={finalStartIcon}
        endIcon={finalEndIcon}
        onClick={onClick}
        sx={buttonSx}
        className={className}
      >
        {finalLabel}
      </Button>
    </Tooltip>
  );
};
export default ExternalLinkButton;
